import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { removeFromWishlist } from '../../actions';
import { getCartTotal } from '../../services';
import AuthService from '../services/auth.service';
import ApiService from '../services/api.service';
import Swal from 'sweetalert2';
import './RegisterAddress.css';

const LIMA = { lat: -12.0464, lng: -77.0428 };

const RegisterAddress = ({ history }) => {
  const [address, setAddress]             = useState('');
  const [reference, setReference]         = useState('');
  const [searchQuery, setSearchQuery]     = useState('');
  const [suggestions, setSuggestions]     = useState([]);
  const [showDropdown, setShowDropdown]   = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selected, setSelected]           = useState(null);
  const [pinCoords, setPinCoords]         = useState(null); // { lat, lng }
  const [geocodedAddress, setGeocodedAddress] = useState('');
  const [geocoding, setGeocoding]         = useState(false);
  const [showManualUrl, setShowManualUrl] = useState(false);
  const [manualUrl, setManualUrl]         = useState('');
  const [mapsError, setMapsError]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [errors, setErrors]               = useState({});

  const mapDivRef      = useRef(null);
  const gmapRef        = useRef(null);
  const gmarkerRef     = useRef(null);
  const debounceRef    = useRef(null);
  const geocodeRef     = useRef(null);

  useEffect(() => {
    document.body.classList.add('dark');
  }, []);

  // Identical to EditCoordinatesModal: initialize map on mount, pin invisible
  useEffect(() => {
    if (!mapDivRef.current || gmapRef.current) return;
    if (!window.google || !window.google.maps) {
      setMapsError(true);
      return;
    }
    try {
      gmapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: LIMA,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });
      gmarkerRef.current = new window.google.maps.Marker({
        map: gmapRef.current,
        draggable: true,
        visible: false,
      });
      gmarkerRef.current.addListener('dragend', function() {
        var p = gmarkerRef.current.getPosition();
        var lat = p.lat();
        var lng = p.lng();
        setSelected(null);
        setPinCoords({ lat: lat, lng: lng });
        triggerReverseGeocode(lat, lng);
      });
      gmapRef.current.addListener('click', function(e) {
        gmarkerRef.current.setPosition(e.latLng);
        gmarkerRef.current.setVisible(true);
        var lat = e.latLng.lat();
        var lng = e.latLng.lng();
        setSelected(null);
        setPinCoords({ lat: lat, lng: lng });
        triggerReverseGeocode(lat, lng);
      });
      // Detect Maps auth error
      var observer = new MutationObserver(function() {
        if (mapDivRef.current && mapDivRef.current.querySelector('.gm-err-container')) {
          setMapsError(true);
          observer.disconnect();
        }
      });
      observer.observe(mapDivRef.current, { childList: true, subtree: true });
    } catch (e) {
      setMapsError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var triggerReverseGeocode = function(lat, lng) {
    clearTimeout(geocodeRef.current);
    setGeocodedAddress('');
    geocodeRef.current = setTimeout(async function() {
      setGeocoding(true);
      try {
        var user = AuthService.getCurrentUser();
        var addr = await ApiService.reverseGeocode(user.token, lat, lng);
        setGeocodedAddress(addr || '');
      } catch (e) {
        // silencioso
      } finally {
        setGeocoding(false);
      }
    }, 600);
  };

  var moveMapTo = function(lat, lng) {
    if (!gmapRef.current || !gmarkerRef.current) return;
    var pos = { lat: lat, lng: lng };
    gmapRef.current.setCenter(pos);
    gmapRef.current.setZoom(17);
    gmarkerRef.current.setPosition(pos);
    gmarkerRef.current.setVisible(true);
    setPinCoords(pos);
    triggerReverseGeocode(lat, lng);
  };

  var handleSearchChange = function(e) {
    var value = e.target.value;
    setSearchQuery(value);
    setSelected(null);
    setShowDropdown(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async function() {
      setSearchLoading(true);
      var user = AuthService.getCurrentUser();
      var results = await ApiService.placesAutocomplete(user.token, value);
      setSuggestions(results);
      setShowDropdown(true);
      setSearchLoading(false);
    }, 500);
  };

  var handleSelectSuggestion = async function(suggestion) {
    setShowDropdown(false);
    setSearchQuery(suggestion.description);
    setSearchLoading(true);
    setSuggestions([]);
    var user = AuthService.getCurrentUser();
    var details = await ApiService.placeDetails(user.token, suggestion.place_id);
    setSearchLoading(false);
    if (!details) return;
    if (!address) setAddress(details.formatted_address);
    setSelected({ description: suggestion.description, url_google: details.url_google });
    setErrors(function(p) { return Object.assign({}, p, { location: undefined }); });
    moveMapTo(details.latitude, details.longitude);
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    var errs = {};
    if (!address.trim()) errs.address = 'La dirección es obligatoria';
    var hasUrl = (selected && selected.url_google) || manualUrl.trim() || pinCoords;
    if (!hasUrl) errs.location = 'Busca tu dirección en Google o pega el link de Google Maps';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    var user = AuthService.getCurrentUser();
    var coordUrl = pinCoords
      ? 'https://www.google.com/maps/search/?api=1&query=' + pinCoords.lat + ',' + pinCoords.lng
      : '';
    var payload = {
      address:   address.trim(),
      ubigeo:    '',
      url_google: (selected && selected.url_google) || manualUrl.trim() || coordUrl,
      id_user:   user.id,
      latitude:  pinCoords ? pinCoords.lat : null,
      longitude: pinCoords ? pinCoords.lng : null,
    };
    try {
      var res = await ApiService.postAddressUser(user.token, payload);
      if (res && res.status === 'success') {
        var Toast = Swal.mixin({
          toast: true, position: 'top-end',
          showConfirmButton: false, timer: 3000, timerProgressBar: true,
        });
        Toast.fire({ icon: 'success', title: '<span style="color:#F7F7F7">Dirección Registrada!</span>' });
        history.push('/welcome');
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Error al guardar la dirección' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cart-container">
      <div className="breadcrumb-section title-cart">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="page-title page-tittle2">
                <label className="text-confirm-order">REGISTRAR DIRECCIÓN</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="section-b-space" style={{ paddingTop: '15px' }}>
        <div className="container padding-cls">
          <div className="checkout-page">
            <div className="checkout-form register-address-form">
              <div className="checkout row container-data-address" style={{ margin: 'auto' }}>
                <div className="col-lg-12 col-sm-12 col-xs-12">
                  <form onSubmit={handleSubmit} autoComplete="off">

                    {/* ── Search ── */}
                    <div className="row check-out row-check-date" style={{ marginBottom: '1rem' }}>
                      <div className="form-group col-md-12 col-sm-12 col-xs-12">
                        <div className="field-label">BUSCAR DIRECCIÓN EN GOOGLE</div>
                        <div className="ra-search-wrapper">
                          <div className="ra-search-input-row">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Ej: Ca. Los Pinos 156, Miraflores"
                              value={searchQuery}
                              onChange={handleSearchChange}
                              onFocus={function() { if (suggestions.length) setShowDropdown(true); }}
                              onBlur={function() { setTimeout(function() { setShowDropdown(false); }, 150); }}
                              autoComplete="off"
                            />
                            {searchLoading && <span className="ra-search-spinner">⏳</span>}
                          </div>

                          {showDropdown && (
                            <ul className="ra-dropdown">
                              {suggestions.length === 0
                                ? <li className="ra-dropdown-empty">Sin resultados para esta búsqueda</li>
                                : suggestions.map(function(s) {
                                    return (
                                      <li key={s.place_id} className="ra-dropdown-item"
                                          onMouseDown={function() { handleSelectSuggestion(s); }}>
                                        <span className="ra-dropdown-item-icon">📍</span>
                                        {s.description}
                                      </li>
                                    );
                                  })
                              }
                            </ul>
                          )}

                          {selected && (
                            <div className="ra-place-confirmed">
                              <span className="ra-place-confirmed-icon">✅</span>
                              <span>{selected.description}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Map ── */}
                    <div className="ra-map-section">
                      <div className="ra-map-label">UBICACIÓN EN EL MAPA</div>
                      <div className="ra-map-container" style={{ position: 'relative', minHeight: '260px', display: 'block', overflow: 'hidden' }}>
                        <div ref={mapDivRef} className="ra-map-div" style={{ width: '100%', height: '260px', display: 'block' }} />
                        {!pinCoords && !mapsError && (
                          <div className="ra-map-placeholder"
                               style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: 'auto', zIndex: 1 }}>
                            <span className="ra-map-placeholder-icon">🗺️</span>
                            <span>Busca tu dirección arriba para ver tu ubicación en el mapa</span>
                          </div>
                        )}
                        {mapsError && (
                          <div className="ra-map-placeholder"
                               style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: 'auto', zIndex: 1 }}>
                            <span className="ra-map-placeholder-icon">⚠️</span>
                            <span>No se pudo cargar Google Maps.<br />Usa el link manual abajo.</span>
                          </div>
                        )}
                      </div>

                      {pinCoords && (
                        <>
                          <div className="ra-map-hint">
                            📌 Arrastra el pin o toca el mapa para ajustar la ubicación
                          </div>
                          {(geocoding || geocodedAddress) && (
                            <div className="ra-geocoded-preview">
                              {geocoding ? '⌛ Obteniendo dirección...' : ('📍 ' + geocodedAddress)}
                            </div>
                          )}
                          <div className="ra-coords-row">
                            <span className="ra-coords-badge">Lat {pinCoords.lat.toFixed(6)}</span>
                            <span className="ra-coords-badge">Lng {pinCoords.lng.toFixed(6)}</span>
                          </div>
                        </>
                      )}

                      {errors.location && (
                        <div className="ra-field-error" style={{ marginTop: 6 }}>⚠ {errors.location}</div>
                      )}
                    </div>

                    <hr className="ra-section-divider" />

                    {/* ── Address ── */}
                    <div className="row check-out row-check-date" style={{ marginBottom: '1rem' }}>
                      <div className="form-group col-md-12 col-sm-12 col-xs-12">
                        <div className="field-label">DIRECCIÓN</div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ej: Ca. Los Pinos 156, Dpto 3B"
                          value={address}
                          onChange={function(e) {
                            setAddress(e.target.value);
                            setErrors(function(p) { return Object.assign({}, p, { address: undefined }); });
                          }}
                        />
                        {errors.address && <div className="ra-field-error">{errors.address}</div>}
                      </div>
                    </div>

                    {/* ── Reference ── */}
                    <div className="row check-out row-check-date" style={{ marginBottom: '1.5rem' }}>
                      <div className="form-group col-md-12 col-sm-12 col-xs-12">
                        <div className="field-label">REFERENCIA</div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ej: Edificio azul, timbre 3"
                          value={reference}
                          onChange={function(e) { setReference(e.target.value); }}
                        />
                      </div>
                    </div>

                    {/* ── Manual URL fallback ── */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <button type="button" className="ra-manual-toggle-btn"
                              onClick={function() { setShowManualUrl(function(v) { return !v; }); }}>
                        {showManualUrl ? '▲ Ocultar' : '¿No encuentras tu dirección? Pegar link de Google Maps'}
                      </button>
                      {showManualUrl && (
                        <div className="ra-manual-section">
                          <div className="ra-manual-section-label">
                            Abre Google Maps, busca tu ubicación y copia el link aquí
                          </div>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="https://maps.google.com/..."
                            value={manualUrl}
                            onChange={function(e) {
                              setManualUrl(e.target.value);
                              setErrors(function(p) { return Object.assign({}, p, { location: undefined }); });
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* ── Submit ── */}
                    {errors.submit && (
                      <div className="ra-form-error-banner">{errors.submit}</div>
                    )}
                    <div className="row check-out row-check-date" style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <div className="form-group col-md-12">
                        <button type="submit" className="btn-solid btn btn-create-referred btn-primary"
                                disabled={loading}>
                          {loading && <span className="spinner-border spinner-border-sm" style={{ marginRight: 6 }} />}
                          GUARDAR
                        </button>
                      </div>
                    </div>

                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const mapStateToProps = (state) => ({
  cartItems: state.cartList.cart,
  symbol:    state.data.symbol,
  total:     getCartTotal(state.cartList.cart),
});

export default connect(mapStateToProps, { removeFromWishlist })(RegisterAddress);
